"use client";

import * as Dialog from "@radix-ui/react-dialog";
import * as Checkbox from "@radix-ui/react-checkbox";
import { Icon } from "@/components/ui/icon";
import { Button } from "@/components/ui/button";
import { Text } from "@/components/ui/text";
import { useUserConsent, UseUserConsentProps } from "./use-user-consent";

export type UserConsentProps = UseUserConsentProps;

function UserConsent(props: UserConsentProps) {
  const {
    styles,
    open,
    onOpenChange,
    onSubmit,
    isLoading,
    nameRef,
    emailRef,
    data,
    checkboxError,
    setCheckboxError,
    errorClasses,
  } = useUserConsent(props);

  if (!data) return null;

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className={styles.overlay()} />
        <Dialog.Content className={styles.content()}>
          <Dialog.Title asChild>
            <Text size="titleMedium" tag="h2" className={styles.title()}>
              {data.title}
            </Text>
          </Dialog.Title>
          <form onSubmit={onSubmit} noValidate>
            <fieldset className={styles.fieldset()}>
              <Text
                size="labelSmall"
                tag="label"
                className={styles.label()}
                htmlFor="name"
              >
                {data.name_label}
              </Text>
              <input
                className={styles.input({
                  className: errorClasses,
                })}
                id="name"
                name="name"
                type="text"
                ref={nameRef}
                required
              />
            </fieldset>
            <fieldset className={styles.fieldset()}>
              <Text
                size="labelSmall"
                tag="label"
                className={styles.label()}
                htmlFor="email"
              >
                {data.email_label}
              </Text>
              <input
                className={styles.input({ className: errorClasses })}
                id="email"
                name="email"
                type="email"
                ref={emailRef}
                required
              />
            </fieldset>
            <fieldset className={styles.fieldset()}>
              <Text
                size="labelSmall"
                tag="label"
                className={styles.label()}
                htmlFor="phone"
              >
                {data.phone_label}
              </Text>
              <input className={styles.input()} id="phone" name="phone" type="tel" />
            </fieldset>
            <fieldset
              className={styles.fieldset({ className: "flex-row items-center mb-0" })}
            >
              <Checkbox.Root
                className={styles.checkbox({
                  className: checkboxError ? "border-feedback-error-9!" : "",
                })}
                id="accepted-terms"
                name="accepted-terms"
                onCheckedChange={() => {
                  if (checkboxError) setCheckboxError(false);
                }}
              >
                <Checkbox.Indicator className={styles.checkboxIndicator()}>
                  <Icon name="checked" className={styles.checkboxIndicatorIcon()} />
                </Checkbox.Indicator>
              </Checkbox.Root>
              <Text
                size="bodyMedium"
                tag="label"
                htmlFor="accepted-terms"
                className={styles.description({ className: "pl-1" })}
              >
                {data.description}
              </Text>
            </fieldset>
            <div className={styles.actions()}>
              <Button
                appearance="solid"
                color="black"
                fluid="all"
                type="submit"
                disabled={isLoading}
              >
                {isLoading ? (
                  <Icon name="loader" className={styles.loading()} />
                ) : (
                  data.submit_button
                )}
              </Button>
            </div>
          </form>
          <Dialog.Close asChild>
            <button className={styles.closeButton()} aria-label="Close">
              <Icon name="close_circle" className="size-5" />
            </button>
          </Dialog.Close>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

export default UserConsent;
